<template>
    <div class="container" v-if="!displayModalChoixSite">
        <div class="title-container">
            <span>
                {{ $t('administratif.request.title') }}
                <ReglePastille cle="tagNfc.definition" />
            </span>
        </div>
        <div class="content-container">
            <div class="filter-container">
                <div class="filter-site-container" v-if="!isUniqueSite">
                    {{$t('intervention.non-preventive.filter')}}
                    <ButtonBlue
                        :on-click="selectionnerSite"
                        :text="$t('bouton.choix.site')"
                        class="button-confirm btn"
                        :disabled="tableData.length === 0 && isLoading"
                    />
                </div>
                <Filter
                    @search="doFilter"
                    @setLocalisation="this.setLocalisation"
                    @clearFilter="this.clearFilter"
                    :filter="this.filter"
                    :searchSite="searchSite"
                />
            </div>
            <div class="dataTable">
                <div class="headerWrapper">
                    <h2>
                        {{ $tc('administratifRequest.dataTable.title', +count) }}
                        <ReglePastille cle="tagNfc.portee" />
                    </h2>
                    <BorderedIconButton v-if="!isExportDisabled" @click="exportXls" :imgUrl="iconeXls" :title="$t('list.export')" />
                </div>
                <LoaderComponent v-if="tableData.length === 0 && isLoading" class="loader" />

                <div v-else @scroll="handleScroll" ref="content" class="datatable-container">
                    <DataTable
                        :count="+count"
                        :tableData="this.tableData"
                        :isLoading="isLoading"
                        :tableHeader="this.tableHeader"
                        :noDataText="$t('search.notFound')"
                        @sort="doSort"
                        @select="getHistorique"
                    />
                </div>
            </div>
        </div>
    </div>
    <ModalChoixSite v-else
                    @cancel="modalChoixSiteCancel"
                    @valider="validerSite"
                    :selected-site="null"
                    :selected-sites="filter.siteList"
                    :is-multi-site="true"/>
    <Modal v-if="isModalHistoriqueOpen" @cancel="isModalHistoriqueOpen = false">
        <ModalCard
            @cancel="isModalHistoriqueOpen = false"
            :noPaddingTop="false"
            :withoutBotNav="true"
            :headerTitle="$t('administratif.navigation.nfc.historique')"
            :active-validate="false"
            :is-pop-up-table="true"
            :fixed-size="true"
        >
            <template #header-append>
                <ReglePastille cle="tagNfc.historique" placement="bottom" />
            </template>
            <div @scroll="handleHistoriqueScroll" ref="contentHisto" class="datatable-historique-container">
                <DataTable
                    :count="+count"
                    :tableData="this.historiqueTableData"
                    :isLoading="isHistoriqueLoading"
                    :tableHeader="this.historiqueTableHeader"
                    :noDataText="$t('search.notFound')"
                    @sort="doSortHistorique"/>
            </div>
        </ModalCard>
    </Modal>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue';
import Filter from '@/modules/Common/components/Filter.vue';
import DataTable from '@/components/ui-components/DataTable.vue';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import SiteListService, { SiteSearchDTO } from '@/modules/SiteList/service/SiteListService';
import { NFC } from '../store/mutation-types';
import { SortType, sortValue } from '@/models/SortType';
import { NFCActions } from '@/modules/Administrative/store/action-types';
import { Site } from '@/models/Site';
import { TableHeader } from '@/models/TableHeader';
import { NFCInformations } from '@/models/NFCInformations';
import ModalChoixSite from '@/modules/Common/components/ModalChoixSite.vue';
import { store } from '@/plugins/store';
import ButtonBlue from '@/components/ui-components/ButtonBlue.vue';
import Modal from '@/components/ui-components/Modal.vue';
import ModalCard from '@/components/ui-components/ModalCard.vue';
import { NFCHistorique } from '@/models/NFCHistorique';
import { HistoriqueNFC, Pageable } from '@/modules/Administrative/services/NFCService';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import ReglePastille from '@/components/regles/ReglePastille.vue';

interface Data {
    siteList: any[];
    filter: {
        siteList: any[];
        localisationList: string;
    };
    siteListTotal: any[];
    debounce: number;
    searchSite: string;
    displayModalChoixSite: boolean;
    isUniqueSite: boolean;
    isModalHistoriqueOpen: boolean;
}

export default defineComponent({
    name: 'TagNfcPage',
    components: {
        BorderedIconButton,
        Filter,
        DataTable,
        LoaderComponent,
        ButtonBlue,
        ModalChoixSite,
        Modal,
        ModalCard,
        ReglePastille,
    },
    data(): Data{
        return {
            siteList: [],
                filter: {
                    siteList: [],
                    localisationList: '',
            },
            siteListTotal: [],
            debounce: 0,
            searchSite: '',
            displayModalChoixSite: false,
            isUniqueSite: false,
            isModalHistoriqueOpen: false,
        };
    },
    setup() {
        const content = ref<Element>();
        const contentHisto = ref<Element>();

        onMounted(() => {
            content.value;
            contentHisto.value;
        });

        return { content, contentHisto };
    },
    beforeMount() {
        this.filter.siteList = this.filterSite;
        SiteListService.getSiteList(new SiteSearchDTO(0, '', true)).then(res => {
            if (res.data.length === 1) {
                this.isUniqueSite = true;
                this.displayModalChoixSite = false;
            }
            this.siteList = res.data.map(
                ({id, chantierId, description}: {id: string; chantierId: number; description: string}) => ({
                    id,
                    chantierId,
                    description,
                })
            );

        this.siteListTotal = this.siteList;
        });

        this.filter.localisationList = this.filterLocalisation;
        if (!this.displayModalChoixSite) {
            this.getNFCData();
        }
    },
    mounted() {
        this.content?.scrollTo({top: this.scroll});
        this.contentHisto?.scrollTo({top: this.scrollHisto});
    },
    methods: {
        getNFCData(page=0) {
            page === 0 && this.content?.scrollTo({top: 0});
            this.$store.dispatch(`NFCState/${NFCActions.GET_NFC}`, {
                page,
                sort: this.sort && sortValue(this.sort.sortData, this.sort.sortStatus),
                localisation: this.filterLocalisation !== '' ? this.filterLocalisation : undefined,
                isActif: true,
                siteIds: this.filterSite?.length === this.siteListTotal?.length ? [] : [...new Set(this.filterSite?.map(x => x.id))],
            });
        },
        exportXls() : void {
            this.$store.dispatch(`NFCState/${NFCActions.GET_XLS_NFC}`, {
                sort: this.sort && sortValue(this.sort.sortData, this.sort.sortStatus),
                localisation: this.filterLocalisation !== '' ? this.filterLocalisation : undefined,
                isActif: true,
                siteIds: this.filterSite?.length === this.siteListTotal?.length ? [] : [...new Set(this.filterSite?.map(x => x.id))],
            });
        },
        doFilter() {
            this.$store.commit(`NFCState/${NFC.RESET_ITEMS}`);

            this.$store.commit(`NFCState/${NFC.SET_NFC_FILTER_SITE}`, this.filter.siteList);
            this.$store.commit(`NFCState/${NFC.SET_FILTER_LOCALISATION}`, this.filter.localisationList);

            this.getNFCData();
        },
        doSort(sort: { sortData: string; sortStatus: SortType }) {
            const { sortData, sortStatus } = sort;
            this.$store.commit(`NFCState/${NFC.SET_SORT}`, { sortData, sortStatus });
            this.$store.commit(`NFCState/${NFC.RESET_ITEMS}`);
            this.getNFCData();
        },
        clearFilter() {
            this.filter.siteList = this.siteListTotal ?? [];
            this.filter.localisationList = '';
            this.doFilter();
        },
        handleScroll() {
            const top = this.content?.scrollTop ?? 0;
            const max = this.content?.scrollHeight ?? 0;
            const height = this.content?.clientHeight ?? 0;
            store.commit(`NFCState/${NFC.SET_SCROLL}`, top);
            if (max - top <= height * 1.3 && !this.isLoading && this.page + 1 < this.numberOfPages) {
                this.$store.commit(`NFCState/${NFC.SET_PAGE}`, this.page + 1);
                this.getNFCData(this.page);
            }
        },
        setLocalisation(value: string): void {
            this.filter.localisationList = value;
        },
        validerSite(sites: Site[]) : void {
            this.siteList = sites;
            this.displayModalChoixSite = false;
            store.commit(`NFCState/${NFC.SET_NFC_FILTER_SITE}`, sites);
            store.commit(`NFCState/${NFC.RESET_ITEMS}`);
            this.getNFCData();
        },
        modalChoixSiteCancel(): void {
            this.siteList = this.siteListTotal;
            this.displayModalChoixSite = false;
            if (this.tableData.length === 0 && !this.isLoading) this.getNFCData();
        },
        selectionnerSite() : void {
            this.displayModalChoixSite = true;
        },
        getHistorique(item: any, page=0) {
            this.isModalHistoriqueOpen = true;
            page === 0 && this.content?.scrollTo({top: 0});
            if (item?.numeroNfc !== undefined) {
                this.$store.commit(`NFCState/${NFC.SET_HISTORIQUE_SORT}`, { sortData: 'dateCreation', sortStatus: SortType.DESC });
            }
            const pageable : Pageable = {
              page,
              sort: this.sortHistorique && sortValue(this.sortHistorique.sortData, this.sortHistorique.sortStatus),
            };
            const payload: HistoriqueNFC = {
                numeroNFC: item?.numeroNfc !== undefined ? item.numeroNfc : this.$store.getters['NFCState/getNumeroNFC'],
                pageable: pageable,
            };
            this.$store.dispatch(`NFCState/${NFCActions.GET_HISTORIQUE_NFC}`, payload);
            this.content?.scrollTo({top: this.scroll});
        },
        doSortHistorique(sort: { sortData: string; sortStatus: SortType }) {
            const { sortData, sortStatus } = sort;
            store.commit(`NFCState/${NFC.RESET_HISTORIQUE}`);
            this.$store.commit(`NFCState/${NFC.SET_HISTORIQUE_SORT}`, { sortData, sortStatus });
            this.getHistorique(undefined);
        },
        handleHistoriqueScroll() {
            const top = this.contentHisto?.scrollTop ?? 0;
            const max = this.contentHisto?.scrollHeight ?? 0;
            const height = this.contentHisto?.clientHeight ?? 0;
            store.commit(`NFCState/${NFC.SET_HISTORIQUE_SCROLL}`, top);
            if (max - top <= height * 1.5 && !this.isHistoriqueLoading && this.historiquePage + 1 < this.numberOfhistoriquePage) {
                this.$store.commit(`NFCState/${NFC.SET_HISTORIQUE_PAGE}`, this.historiquePage + 1);
                this.getHistorique(this.historiquePage, this.historiquePage);
            }
        },
    },
    computed: {
        tableData() {
            return this.$store.getters['NFCState/getNFCItems'].map(
                ({localisation, numeroNfc, adresse, site}: NFCInformations) => {
                    const adresseSite = (adresse.numero ? (adresse.numero + ' ') : '') + (adresse.natureVoie ? (adresse.natureVoie + ' ') : '') + (adresse.nomVoie ? (adresse.nomVoie + ' ') : '') + '\n' + (adresse.codePostal ? (adresse.codePostal + ' ') : '') + (adresse.bureauDistributeur ? (adresse.bureauDistributeur + ' ') : '');

                    return {
                        adresse: adresseSite,
                        numeroNfc: numeroNfc,
                        localisation: localisation,
                        site: site
                    };
                }
            );
        },
        tableHeader(): TableHeader[] {
            return this.$store.getters['NFCState/getNFCTableHeader'];
        },
        historiqueTableData() {
            return this.$store.getters['NFCState/getNFCHistorique'].map(
                ({libelleAdresseExecution, localisation, dateCreation}: NFCHistorique) => {
                    return {
                        libelleAdresseExecution: libelleAdresseExecution,
                        localisation: localisation,
                        dateCreation: dateCreation,
                    };
                }
            );
        },
        historiqueTableHeader(): TableHeader[] {
            return this.$store.getters['NFCState/getHistoriqueNFCTableHeader'];
        },
        isLoading() {
            return this.$store.getters['NFCState/getNFCIsLoading'];
        },
        page() {
            return this.$store.getters['NFCState/getNFCPage'];
        },
        numberOfPages() {
            return this.$store.getters['NFCState/getNFCNbOfPages'];
        },
        scroll() {
            return this.$store.getters['NFCState/getNFCScroll'];
        },
        scrollHisto() {
            return this.$store.getters['NFCState/getHistoriqueScroll'];
        },
        filterSite(): any[] {
            return this.$store.getters['NFCState/getNFCFilterSite'];
        },
        filterLocalisation() {
            return this.$store.getters['NFCState/getNFCFilterLocalisation'];
        },
        count() {
            return this.$store.getters['NFCState/getNFCCount'];
        },
        sort(): {
            sortData: string;
            sortStatus: SortType;
        } {
            return this.$store.getters['NFCState/getNFCSort'];
        },
        sortHistorique(): {
            sortData: string;
            sortStatus: SortType;
        } {
            return this.$store.getters['NFCState/getHistoriqueSort'];
        },
        numberOfhistoriquePage(): number {
            return this.$store.getters['NFCState/getNbOfHistoriquePage'];
        },
        historiquePage(): number {
            return this.$store.getters['NFCState/getHistoriquePage'];
        },
        isHistoriqueLoading() {
            return this.$store.getters['NFCState/getHistoriqueIsLoading'];
        },
        iconeXls() {
            return require('@/assets/img/xls.svg');
        },
        isExportDisabled(): boolean {
            return this.tableData.length === 0;
        },
        isExportXlsLoading(): boolean {
            return store.getters.getIsExportXlsLoading;
        }
    }
});
</script>

<style lang="scss" scoped>
@import '@/theme/pages-recherche.scss';

@media screen and (max-width: 1631px) {
    .datatable-container {
        max-height: calc(100vh - 340px);
    }
}

@media screen and (max-width: 1536px) {
    .datatable-container {
        max-height: calc(100vh - 440px);
    }
}

@media screen and (max-width: 970px) {
    .datatable-container {
        max-height: calc(100vh - 518px);
    }
}

:deep(.containerBtn) {
    height: 23px;
}
:deep(.icon) {
    height: 20px;
}
.filter-container {
    display: flex;
    padding: 8px 16px;
    background: white;
    border-radius: 6px;
    margin-bottom: 8px;
}
.filter-site-container{
    flex-direction: column;
    width: 15%;
}
.filter {
    width: 100%;
}
.button-confirm {
    right: 16px;
}
.btn {
    width: 100%;
    border-radius: 5px;
    cursor: pointer;
    height: 40px;
    margin-top: 20px;
}
:deep(.filter) {
    margin-bottom: 0;
    padding: 0 16px;
}
.datatable-historique-container {
    max-height: 50vh;
    overflow: auto;
}
</style>
